import React from "react";
import { Button } from "react-bootstrap";

/**
 * ColumnTranslator - Converts normalized column schema (from DB) to library-specific formats.
 * Normalized schema: [{ field, header, sortable, type, width, format, ... }]
 */

/**
 * Convert normalized columnsConfig to react-data-table-component columns
 * @param {Array} columns - Array of column config objects from DB
 * @param {Object} options - { onEdit, onDelete, actionsConfig }
 * @returns {Array} RDT-compatible column definitions
 */
export function toRDTColumns(columns, options = {}) {
  if (!columns || !Array.isArray(columns)) return [];

  const rdtCols = columns.map((col) => {
    const rdtCol = {
      name: col.header || col.field,
      selector: (row) => row[col.field],
      sortable: col.sortable !== false,
    };

    if (col.width) {
      rdtCol.width = col.width;
    }

    // Cell renderer based on type
    rdtCol.cell = (row) => {
      const value = row[col.field];
      switch (col.type) {
        case "date":
          return <span>{value ? new Date(value).toLocaleDateString() : ""}</span>;
        case "number":
          return <span>{value != null ? Number(value).toLocaleString() : ""}</span>;
        case "boolean":
          return (
            <span className={value === "1" || value === 1 ? "text-success" : "text-danger"}>
              {value === "1" || value === 1 ? "Yes" : "No"}
            </span>
          );
        default:
          return <span>{value}</span>;
      }
    };

    return rdtCol;
  });

  // Add actions column if configured
  const actionsConfig = options.actionsConfig;
  if (actionsConfig && (actionsConfig.canEdit || actionsConfig.canDelete)) {
    rdtCols.push({
      name: "Actions",
      sortable: false,
      cell: (row) => (
        <div>
          {actionsConfig.canEdit && (
            <Button
              variant="primary"
              className="bi bi-pen"
              style={{ marginRight: "3px" }}
              size="sm"
              onClick={() => options.onEdit && options.onEdit(row)}
            />
          )}
          {actionsConfig.canDelete && (
            <Button
              variant="danger"
              className="bi bi-trash"
              size="sm"
              onClick={() => options.onDelete && options.onDelete(row)}
            />
          )}
        </div>
      ),
    });
  }

  return rdtCols;
}

/**
 * Convert normalized columnsConfig to PrimeReact DataTable columns
 * @param {Array} columns - Array of column config objects from DB
 * @returns {Array} PrimeReact-compatible column definitions
 */
export function toPrimeColumns(columns) {
  if (!columns || !Array.isArray(columns)) return [];

  return columns.map((col) => ({
    field: col.field,
    header: col.header || col.field,
    sortable: col.sortable !== false,
    style: col.width ? { width: col.width } : undefined,
    body: col.type === "date"
      ? (rowData) => (rowData[col.field] ? new Date(rowData[col.field]).toLocaleDateString() : "")
      : col.type === "boolean"
      ? (rowData) => (rowData[col.field] === "1" || rowData[col.field] === 1 ? "Yes" : "No")
      : undefined,
  }));
}

export default { toRDTColumns, toPrimeColumns };
